import { Typography, Box, Divider, Alert, Button } from "@mui/material";
import {
	CalendarToday as CalendarIcon,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { useCallback, useEffect, useMemo, useReducer, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useToast } from "../components/common/custom-component/ToastProvider";

import { useNavigate } from "react-router-dom";

import LoadingBackdrop from "../components/common/custom-component/LoadingBackdrop";
import DataContainer, { TableActionButton, TableActionGroup } from "../components/common/DataContainer";
import { useSearchFilter } from "../hooks/useSearchFilter";
import EnhancedConfirmationDialog from "../components/common/custom-component/EnhancedConfirmationDialog";
import SearchFilterBar from "../components/common/SearchFilterBar";
import CustomPagination from "../components/common/CustomPagination";
import { logger } from "helpers/logger";

import { manageGeneratorsReducer, initialState } from "../reducers/manageGeneratorsReducer";
import { useTableColumns } from "../hooks/useTableColumns";
import {
	addTransporterGeneratorThunk,
	deleteTransporterGeneratorThunk,
	editTransporterGeneratorThunk,
	getCRDetailsThunk,
	addTransporterGeneratorContactThunk,
	fetchTransporterGeneratorThunk,
} from "store/thunks/mdsThunks";
import RefreshIcon from "@mui/icons-material/Refresh";

export default function ManageGeneratorsPage() {
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const [t] = useTranslation();
	const { success: ToastSuccess, error: ToastError } = useToast();

	const selectedCompany = useSelector((state) => state.selectedCompany.data?.companyData);

	const [state, dispatchState] = useReducer(manageGeneratorsReducer, initialState);

	const displayData = useMemo(() => Array.isArray(state?.data?.generators) ? state?.data?.generators : state?.data?.generators?.payload, [state?.data?.generators]);

	const [retryTrigger, setRetryTrigger] = useState(0);
	const [error, setError] = useState(null);
	const handleRetry = () => {
		setRetryTrigger(prev => prev + 1);
	};

	const errorView = error ? (
		<Box sx={{ p: 4, mx: "auto", display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
			<Alert severity="error" sx={{ width: "100%", maxWidth: 600 }}>
				{error}
			</Alert>
			<Button variant="contained" color="primary" startIcon={<RefreshIcon />} onClick={handleRetry}>
				Retry
			</Button>
		</Box>
	) : null;

	// #region ADD DELEGATE
	const [addDelegateOpen, setAddDelegateOpen] = useState(false);
	const [isDelegateLoading, setIsDelegateLoading] = useState(false);

	const defaultAddDelegateForm = useMemo(
		() => ({
			generatorCompanyId: "",
			companyId: "",
			firstName: "",
			lastName: "",
			requiredID: false,
			nationalId: "",
			mobileNumber: "",
			address: "",
			email: "",
			type: "",
			other: "",
		}),
		[]
	);

	const [addDelegateForm, setAddDelegateForm] = useState(defaultAddDelegateForm);

	const generatorOptions = useMemo(() => {
		return [
			{ value: "", label: t("Select") },
			{ value: "0", label: t("Individual") },
			...((displayData || []).map((g) => ({
				value: `${g?.id ?? ""}`,
				label: t(g?.companyName ?? ""),
				generator: g,
			})) ?? []),
		];
	}, [displayData, t]);


	const handleAddDelegateClick = useCallback(() => {
		setAddDelegateForm(defaultAddDelegateForm);
		setAddDelegateOpen(true);
	}, [defaultAddDelegateForm]);

	const handleCancelAddDelegate = useCallback(() => {
		setAddDelegateOpen(false);
	}, []);

	const handleConfirmAddDelegate = useCallback(
		async (formData) => {
			if (!selectedCompany?.companyId) {
				ToastError(t("Company not selected"));
				return;
			}

			setIsDelegateLoading(true);

			try {
				const payload = {
					...formData,
					companyId: formData.companyId || selectedCompany.companyId,
					generatorCompanyId: formData.generatorCompanyId,
				};

				const result = await dispatch(
					addTransporterGeneratorContactThunk({
						companyId: selectedCompany.companyId,
						data: payload,
					})
				);

				if (addTransporterGeneratorContactThunk.rejected.match(result)) {
					ToastError(t("An error occured, please try again later."));
					return;
				}

				ToastSuccess(t("Success"));
				setAddDelegateOpen(false);
			} catch (err) {
				ToastError(t("An error occured, please try again later."));
			} finally {
				setIsDelegateLoading(false);
			}
		},
		[dispatch, selectedCompany?.companyId, t]
	);
	// #endregion

	// #region FETCH DATA
	useEffect(() => {
		let promiseRef = null;

		const fetchData = async () => {
			// Reset states when retrying
			setError(null);
			dispatchState({ type: "SET_LOADING", payload: { key: "isLoadingBackdrop", value: true } });

			promiseRef = dispatch(fetchTransporterGeneratorThunk({ companyId: selectedCompany.companyId }));

			const recordResult = await promiseRef;

			if (fetchTransporterGeneratorThunk.fulfilled.match(recordResult)) {
				const records = recordResult?.payload?.data;
				dispatchState({ type: "SET_GENERATORS", payload: Array.isArray(records) ? records : [] });
				dispatchState({ type: "SET_LOADING", payload: { key: "isLoadingBackdrop", value: false } });
			} else if (fetchTransporterGeneratorThunk.rejected.match(recordResult)) {
				// Check if it's not a cancellation
				if (recordResult.payload !== 'cancelled') {
					setError(recordResult.payload || 'An error occurred while loading data');
					logger.error('An error occurred while loading data');
				}
				dispatchState({ type: "SET_LOADING", payload: { key: "isLoadingBackdrop", value: false } });
			}
		};

		fetchData();

		// Cleanup: abort on unmount - just like saga!
		return () => {
			if (promiseRef) {
				logger.info('🛑 Component unmounting, aborting request');
				promiseRef.abort();
			}
		};
	}, [dispatch, selectedCompany?.companyId, retryTrigger]);
	// #endregion

	// #region SEARCH & PAGINATION
	const { setSearchQuery, filteredData } = useSearchFilter(
		displayData,
		["companyName", "generatedWasteType", "wasteType", "unn", "crNumber", "address", "phoneNumber"],
		null
	);

	const totalPages = Math.max(1, Math.ceil((filteredData?.length || 0) / state.table.recordsPerPage));

	const pagedData = useMemo(() => {
		const start = (state.table.currentPage - 1) * state.table.recordsPerPage;
		const end = start + state.table.recordsPerPage;
		return (filteredData || []).slice(start, end);
	}, [filteredData, state.table.currentPage, state.table.recordsPerPage]);
	// #endregion

	// #region HELPER FUNCTION
	const isUNNUnique = useCallback(
		(unn, excludeId = null) => {
			const list = displayData || [];
			return !list.some((g) => g?.unn === unn && (excludeId ? g?.id !== excludeId : true));
		},
		[displayData]
	);

	const validateBusinessRules = useCallback(
		(form, mode, excludeId = null) => {
			const generatedWasteType = (form.generatedWasteType || "").trim();
			const wasteType = (form.wasteType || "").trim();
			const unn = (form.unn || "").trim();
			const crNumber = (form.crNumber || "").trim();
			const address = (form.address || "").trim();
			const companyName = (form.companyName || "").trim();

			if (!isUNNUnique(unn, mode === "edit" ? excludeId : null))
				return ToastError(t("UNN already exists. Please enter a new UNN.")), false;

			if (generatedWasteType === "Hazardous Waste") {
				if (wasteType !== "Government") {
					if (!crNumber)
						return (
							ToastError(t("CR Number is required for Hazardous Waste when Company Type is not Government.")),
							false
						);
				}

				if (address.length === 0)
					return ToastError(t("Address is required for Hazardous Waste.")), false;
			}

			if (companyName.length === 0)
				return ToastError(t("Facility Name is required.")), false;

			return true;
		},
		[isUNNUnique, t]
	);
	// #endregion

	// #region Add GENERATOR
	const handleAddClick = async () => {
		dispatchState({ type: "OPEN_ADD_DIALOG" });
		dispatchState({ type: "SET_ADD_UNN_VERIFY_ERROR", payload: "" });
	};

	const handleVerifyUNN = useCallback(
		async () => {
			const unn = (state.forms.addGeneratorForm?.unn || "").trim();

			if (!unn || unn.length !== 10) {
				dispatchState({ type: "SET_ADD_UNN_VERIFY_ERROR", payload: t("UNN must be exactly 10 digits") });
				return;
			}

			dispatchState({ type: "SET_ADD_UNN_VERIFY_ERROR", payload: "" });
			dispatchState({ type: "SET_LOADING", payload: { key: "isLoadingBackdrop", value: true } });

			try {
				const verifyResult = await dispatch(getCRDetailsThunk({ unn }));

				if (getCRDetailsThunk.rejected.match(verifyResult) || !verifyResult.payload) {
					dispatchState({ type: "SET_ADD_UNN_VERIFY_ERROR", payload: t("UNN is not verified") });
					return;
				}

				const responseObject = verifyResult.payload?.data;

				const verifiedFields = {
					companyName: responseObject.companyName ?? "",
					address: responseObject.address ?? "",
					phoneNumber: responseObject.phoneNumber ?? "",
					crNumber: responseObject.crNumber ?? "",
				};

				Object.entries(verifiedFields).forEach(([fieldName, value]) => {
					dispatchState({
						type: "UPDATE_FORM_FIELD",
						payload: { formName: "addGeneratorForm", fieldName, value },
					});
				});
			} catch {
				dispatchState({ type: "SET_ADD_UNN_VERIFY_ERROR", payload: t("UNN is not verified") });
			} finally {
				dispatchState({ type: "SET_LOADING", payload: { key: "isLoadingBackdrop", value: false } });
			}
		},
		[dispatch, state.forms.addGeneratorForm?.unn, t]
	);

	const handleConfirmAdd = useCallback(
		async (formData) => {
			if (!validateBusinessRules(formData, "add")) return;

			dispatchState({ type: "SET_LOADING", payload: { key: "isLoadingBackdrop", value: true } });

			try {
				const payload = { ...formData, isVerified: true };

				const result = await dispatch(
					addTransporterGeneratorThunk({
						companyId: selectedCompany.companyId,
						data: payload,
					})
				);

				if (addTransporterGeneratorThunk.rejected.match(result)) {
					ToastError(t("An error occured, please try again later."));
					return;
				}

				ToastSuccess(t("Success"));
				const records = await dispatch(fetchTransporterGeneratorThunk({ companyId: selectedCompany.companyId }));;
				dispatchState({ type: "SET_GENERATORS", payload: Array.isArray(records.payload?.data) ? records.payload?.data : [] });
				dispatchState({ type: "CLOSE_ADD_DIALOG" });
			} catch (err) {
				ToastError(t("An error occured, please try again later."));
			} finally {
				dispatchState({ type: "SET_LOADING", payload: { key: "isLoadingBackdrop", value: false } });
			}
		},
		[dispatch, selectedCompany?.companyId, t, validateBusinessRules]
	);

	const handleCancelAdd = useCallback(() => {
		dispatchState({ type: "CLOSE_ADD_DIALOG" });
	}, []);
	// #endregion

	// #region EDIT GENERATOR
	const handleEditClick = useCallback(async (row) => {
		let editGeneratorInitialFormData = {
			generatedWasteType: row.generatedWasteType,
			wasteType: row.wasteType,
			unn: row.unn,
			crNumber: row.crNumber,
			address: row.address,
			companyName: row.companyName,
			phoneNumber: row.phoneNumber,
			isVerified: row?.isVerified ?? true,
		};

		dispatchState({
			type: "OPEN_EDIT_DIALOG",
			payload: {
				record: row,
				editFormData: editGeneratorInitialFormData,
				initialEditGeneratorForm: editGeneratorInitialFormData,
			},
		});
	}, []);

	const handleCancelEdit = useCallback(() => {
		dispatchState({ type: "CLOSE_EDIT_DIALOG" });
	}, []);

	const handleConfirmEdit = useCallback(
		async (formData) => {
			if (!validateBusinessRules(formData, "edit", state.dialogs.selectedRecord.id)) return;

			dispatchState({ type: "SET_LOADING", payload: { key: "isLoadingBackdrop", value: true } });

			try {
				const payload = { ...formData, isVerified: true };

				const result = await dispatch(
					editTransporterGeneratorThunk({
						generatorId: state.dialogs.selectedRecord.id,
						data: payload,
					})
				);

				if (editTransporterGeneratorThunk.rejected.match(result)) {
					ToastError(t("An error occured, please try again later."));
					return;
				}

				ToastSuccess(t("Success"));
				const records = await dispatch(fetchTransporterGeneratorThunk({ companyId: selectedCompany.companyId }));;
				dispatchState({ type: "SET_GENERATORS", payload: Array.isArray(records.payload?.data) ? records.payload?.data : [] });
				dispatchState({ type: "CLOSE_EDIT_DIALOG" });
			} catch (err) {
				ToastError(t("An error occured, please try again later."));
			} finally {
				dispatchState({ type: "SET_LOADING", payload: { key: "isLoadingBackdrop", value: false } });
			}
		},
		[dispatch, selectedCompany?.companyId, state.dialogs.selectedRecord, t]
	);
	// #endregion

	// #region VIEW GENERATOR
	const handleViewClick = useCallback((row) => {
		const viewGeneratorInitialFormData = {
			generatedWasteType: row?.generatedWasteType ?? "",
			wasteType: row?.wasteType ?? "",
			unn: row?.unn ?? "",
			crNumber: row?.crNumber ?? "",
			address: row?.address ?? "",
			companyName: row?.companyName ?? "",
			phoneNumber: row?.phoneNumber ?? "",
			isVerified: row?.isVerified ?? true,
		};

		dispatchState({
			type: "OPEN_VIEW_DIALOG",
			payload: {
				record: row,
				viewFormData: viewGeneratorInitialFormData,
			},
		});
	}, []);

	const handleCancelView = useCallback(() => {
		dispatchState({ type: "CLOSE_VIEW_DIALOG" });
	}, []);
	// #endregion

	// #region DELETE GENERATOR
	const handleDeleteClick = useCallback((row) => {
		dispatchState({ type: "OPEN_DELETE_DIALOG", payload: { ...row } });
	}, []);

	const handleCancelDelete = useCallback(() => {
		dispatchState({ type: "CLOSE_DELETE_DIALOG" });
	}, []);

	const handleConfirmDelete = useCallback(async () => {
		if (!state.dialogs.selectedRecord?.id) {
			dispatchState({ type: "CLOSE_DELETE_DIALOG" });
			return;
		}

		dispatchState({ type: "SET_LOADING", payload: { key: "isLoadingBackdrop", value: true } });

		try {
			const result = await dispatch(deleteTransporterGeneratorThunk({ generatorId: state.dialogs.selectedRecord.id }));

			if (deleteTransporterGeneratorThunk.rejected.match(result)) {
				ToastError(t("An error occured, please try again later."));
				return;
			}

			ToastSuccess(t("Deleted"));
			const records = await dispatch(fetchTransporterGeneratorThunk({ companyId: selectedCompany.companyId }));;
			dispatchState({ type: "SET_GENERATORS", payload: Array.isArray(records.payload?.data) ? records.payload?.data : [] });
			dispatchState({ type: "CLOSE_DELETE_DIALOG" });
		} catch (err) {
			ToastError(t("An error occured, please try again later."));
		} finally {
			dispatchState({ type: "SET_LOADING", payload: { key: "isLoadingBackdrop", value: false } });
		}
	}, [dispatch, selectedCompany?.companyId, state.dialogs.selectedRecord, t]);
	// #endregion

	// #region DELEGATES
	const handleDelegatesClick = useCallback(
		(row) => {
			const companyId =
				row?.companyId ??
				row?.id;

			if (!companyId) {
				ToastError(t("Company not found"));
				return;
			}

			const companyName = row?.companyName ?? "";
			const qs = new URLSearchParams({
				filteredCompanyId: `${companyId}`,
				companyName: `${companyName}`,
			}).toString();

			navigate(`/manage-contacts?${qs}`);
		},
		[navigate, t]
	);
	// #endregion

	// #region MANAGE GENERATORS TABLE COLUMNS
	const actions = useMemo(() => ({
		onView: handleViewClick,
		onEdit: handleEditClick,
		onDelegates: handleDelegatesClick,
		onDelete: handleDeleteClick,
	}), [handleViewClick, handleEditClick, handleDelegatesClick, handleDeleteClick]);

	const { columns } = useTableColumns('ManageGenerators', { actions });
	// #endregion

	// #region RETURN COMPONENT
	if (error) return errorView;
	return (
		<Box sx={{ p: 3 }}>
			<Typography variant="h3" gutterBottom color="primary">
				{t("Manage Related Generators")}
			</Typography>

			<Typography variant="body1" color="text.secondary" paragraph>
				{t("This is a Manage Generators page.")}
			</Typography>

			<Divider sx={{ my: 3 }} />

			{!displayData || displayData.length === 0 ? (
				<DataContainer
					data={[]}
					columns={columns}
					emptyStateIcon={CalendarIcon}
					emptyStateTitle={t("No generators found")}
					emptyStateSubtitle={t("Start adding related generators to your company")}
				/>
			) : (
				<>
					<Box sx={{ p: 2 }}>
						<Box sx={{ my: 2, display: "flex", flexDirection: "column", gap: 2 }}>
							<TableActionGroup sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
								<TableActionButton text={t("Add New Generator")} variant="contained" onClick={() => handleAddClick()} />
								<TableActionButton text={t("Add New Delegate")} variant="contained" onClick={handleAddDelegateClick} />
							</TableActionGroup>

							<SearchFilterBar
								mode="frontend"
								searchValue={state.table.searchInput}
								onSearchChange={(v) => {
									dispatchState({ type: "SET_SEARCH_INPUT", payload: v });
									setSearchQuery(v);
								}}
								showFilterButton={false}
								showSearchButton={false}
								searchPlaceholder={t("Search generators...")}
							/>
						</Box>

						<DataContainer data={pagedData} columns={columns} showRowNumbers maxHeight={600} stickyHeader />
					</Box>

					<Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
						<CustomPagination
							currentPage={state.table.currentPage}
							totalPages={totalPages}
							onPageChange={(e, v) => dispatchState({ type: "SET_CURRENT_PAGE", payload: v })}
							showFirstButton
							showLastButton
						/>
					</Box>
				</>
			)}

			{/* ADD DELEGATE DIALOG */}
			<EnhancedConfirmationDialog
				open={addDelegateOpen}
				onClose={handleCancelAddDelegate}
				onConfirm={handleConfirmAddDelegate}
				title={t("Add New Delegate")}
				message={
					<Box component="span" sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
						<Typography component="span" variant="body2" color="text.secondary">
							{t("Add contact to your company")}
						</Typography>
					</Box>
				}
				cancelText={t("Cancel")}
				confirmText={t("Confirm")}
				confirmColor="primary"
				showMessage={true}
				defaultValues={addDelegateForm}
				fields={[
					{
						name: "generatorCompanyId",
						label: "Company",
						type: "dropdown",
						required: true,
						options: generatorOptions,
						onChange: (optionValue) => {
							const selectedOption = generatorOptions.find((o) => `${o.value}` === `${optionValue}`);
							const selectedGenerator = selectedOption?.generator;

							setAddDelegateForm((prev) => {
								const next = {
									...prev,
									generatorCompanyId: optionValue,
								};

								if (selectedGenerator && optionValue !== "0") {
									next.companyId = selectedCompany?.companyId ?? "";
								} else {
									next.companyId = "Individual";
								}

								return next;
							});
						},
					},
					{
						name: "firstName",
						label: "First Name",
						type: "text",
						required: true,
						onChange: (v) => setAddDelegateForm((p) => ({ ...p, firstName: v })),
					},
					{
						name: "lastName",
						label: "Last Name",
						type: "text",
						required: true,
						onChange: (v) => setAddDelegateForm((p) => ({ ...p, lastName: v })),
					},
					{
						name: "requiredID",
						label: t("IdNumberAvailable"),
						type: "radio",
						options: [
							{ value: true, label: t("Yes") },
							{ value: false, label: t("No") },
						],
						onChange: (value) => {
							setAddDelegateForm((prev) => {
								const next = { ...prev, requiredID: value };
								if (value === false) next.nationalId = "";
								return next;
							});
						},
					},
					{
						name: "nationalId",
						label: "National ID/Iqama ID",
						type: "text",
						placeholder: "Enter National ID (10 digits)",
						regex: /^\d{10}$/,
						regexErrorMessage: "National ID must be exactly 10 characters long.",
						inputMode: "numeric",
						pattern: "[0-9]*",
						numericOnly: true,
						disabled: !Boolean(addDelegateForm?.requiredID),
						onChange: (v) => setAddDelegateForm((p) => ({ ...p, nationalId: v })),
					},
					{
						name: "mobileNumber",
						label: "Mobile Number",
						type: "text",
						required: true,
						placeholder: "Enter mobile number (e.g. 0512345678)",
						regex: /^05\d{8}$/,
						regexErrorMessage: "Mobile Number must be 10 digits and start with '05'.",
						inputMode: "numeric",
						pattern: "[0-9]*",
						numericOnly: true,
						onChange: (v) => setAddDelegateForm((p) => ({ ...p, mobileNumber: v })),
					},
					{
						name: "address",
						label: "Address",
						type: "text",
						onChange: (v) => setAddDelegateForm((p) => ({ ...p, address: v })),
					},
					{
						name: "email",
						label: "Email",
						type: "text",
						required: `${addDelegateForm?.generatorCompanyId ?? ""}` !== "0",
						placeholder: "Enter email",
						disabled: `${addDelegateForm?.generatorCompanyId ?? ""}` === "0",
						regex: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
						regexErrorMessage: t("Please enter a valid email address."),
						onChange: (v) => setAddDelegateForm((p) => ({ ...p, email: v })),
					},
				]}
			/>

			{/* DELETE */}
			<EnhancedConfirmationDialog
				open={state.dialogs.deleteConfirm}
				onClose={handleCancelDelete}
				onConfirm={handleConfirmDelete}
				title={t("Remove Generator")}
				message={t("Are you sure you want to remove this generator? This action cannot be undone.")}
				confirmText={t("DELETE")}
				confirmColor="error"
				cancelText={t("Cancel")}
			/>

			{/* EDIT */}
			<EnhancedConfirmationDialog
				open={state.dialogs.editConfirm}
				onClose={handleCancelEdit}
				onConfirm={handleConfirmEdit}
				title={t("Confirm Edit")}
				confirmText={t("Edit")}
				cancelText={t("Cancel")}
				showMessage={false}
				returnChangedFieldsOnly={false}
				defaultValues={state.forms.editGeneratorForm}
				fields={[
					{ name: "generatedWasteType", label: "Generated Waste Type", type: "dropdown", required: true, options: state.ui.generatedWasteTypeOptions },
					{ name: "wasteType", label: "Company Type", type: "dropdown", required: true, options: state.ui.companyTypeOptions, disabled: true },
					{
						name: "unn",
						label: "Unified National Number (UNN)",
						type: "text",
						required: true,
						placeholder: "Enter UNN (10 digits)",
						regex: /^\d{10}$/,
						regexErrorMessage: "UNN must be exactly 10 digits",
						inputMode: "numeric",
						pattern: "[0-9]*",
						numericOnly: true,
						disabled: true,
					},
					{
						name: "crNumber",
						label: "CR Number",
						type: "text",
						placeholder: "Enter CR Number (10 digits)",
						regex: /^(?:\d{10})?$/,
						regexErrorMessage: "CR Number must be exactly 10 digits",
						inputMode: "numeric",
						pattern: "[0-9]*",
						numericOnly: true,
						disabled: true,
						required: state.forms.editGeneratorForm.generatedWasteType === "Hazardous Waste",
					},
					{
						name: "address",
						label: "Address",
						type: "text",
						required: state.forms.editGeneratorForm.generatedWasteType === "Hazardous Waste"
					},
					{
						name: "companyName",
						label: "Facility Name",
						type: "text",
						disabled: true,
						required: true
					},
					{
						name: "phoneNumber",
						label: "Phone Number",
						type: "text",
						required: true,
						placeholder: "Enter phone number (e.g. 0512345678)",
						regex: /^05\d{8}$/,
						regexErrorMessage: "Phone Number must start with 05 and be exactly 10 digits",
						inputMode: "numeric",
						pattern: "[0-9]*",
						numericOnly: true,
					},
				]}
			/>

			{/* VIEW */}
			<EnhancedConfirmationDialog
				open={state.dialogs.viewConfirm}
				onClose={handleCancelView}
				onConfirm={handleCancelView}
				title={t("View Generator")}
				confirmText={t("OK")}
				cancelText={t("Close")}
				showMessage={false}
				defaultValues={state.forms.viewGeneratorForm}
				fields={[
					{ name: "generatedWasteType", label: "Generated Waste Type", type: "dropdown", options: state.ui.generatedWasteTypeOptions, disabled: true },
					{ name: "wasteType", label: "Company Type", type: "dropdown", options: state.ui.companyTypeOptions, disabled: true },
					{ name: "unn", label: "Unified National Number (UNN)", type: "text", disabled: true },
					{ name: "crNumber", label: "CR Number", type: "text", disabled: true },
					{ name: "address", label: "Address", type: "text", disabled: true },
					{ name: "companyName", label: "Facility Name", type: "text", disabled: true },
					{ name: "phoneNumber", label: "Phone Number", type: "text", disabled: true },
				]}
			/>

			{/* ADD GENERATOR */}
			<EnhancedConfirmationDialog
				open={state.dialogs.addConfirm}
				onClose={handleCancelAdd}
				onConfirm={handleConfirmAdd}
				title={t("Add New Generator")}
				message={
					<Box component="span" sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
						<Typography component="span" variant="body2" color="text.secondary">
							{t("Add generator to your company")}
						</Typography>
					</Box>
				}
				cancelText={t("Cancel")}
				confirmText={t("Confirm")}
				confirmColor="primary"
				showMessage={true}
				defaultValues={state.forms.addGeneratorForm || initialState.forms.addGeneratorForm}
				fields={[
					{
						name: "generatedWasteType",
						label: "Generated Waste Type",
						type: "dropdown",
						required: true,
						options: state.ui.generatedWasteTypeOptions,
						onChange: (value) =>
							dispatchState({ type: "UPDATE_FORM_FIELD", payload: { formName: "addGeneratorForm", fieldName: "generatedWasteType", value } }),
					},
					{
						name: "wasteType",
						label: "Company Type",
						type: "dropdown",
						required: true,
						options: state.ui.companyTypeOptions,
						onChange: (value) =>
							dispatchState({ type: "UPDATE_FORM_FIELD", payload: { formName: "addGeneratorForm", fieldName: "wasteType", value } }),
					},
					{
						name: "unn",
						label: "Unified National Number (UNN)",
						type: "text",
						required: true,
						placeholder: "Enter UNN (10 digits)",
						regex: /^\d{10}$/,
						regexErrorMessage: "UNN must be exactly 10 digits",
						error: Boolean(state.ui.addUnnVerifyError),
						helperText: state.ui.addUnnVerifyError || "",
						onChange: (value) => {
							dispatchState({ type: "SET_ADD_UNN_VERIFY_ERROR", payload: "" });
							dispatchState({ type: "UPDATE_FORM_FIELD", payload: { formName: "addGeneratorForm", fieldName: "unn", value } });
						},
					},
					{ name: "verifyUnn", type: "button", text: t("Verify"), variant: "outlined", color: "primary", onClick: handleVerifyUNN },
					{
						name: "crNumber",
						label: "CR Number",
						type: "text",
						placeholder: "Enter CR Number (10 digits)",
						regex: /^(?:\d{10})?$/,
						regexErrorMessage: "CR Number must be exactly 10 digits",
						inputMode: "numeric",
						pattern: "[0-9]*",
						numericOnly: true,
						required: state.forms.addGeneratorForm.generatedWasteType === "Hazardous Waste",
						onChange: (value) =>
							dispatchState({ type: "UPDATE_FORM_FIELD", payload: { formName: "addGeneratorForm", fieldName: "crNumber", value } }),
					},
					{
						name: "address",
						label: "Address",
						type: "text",
						required: state.forms.addGeneratorForm.generatedWasteType === "Hazardous Waste",
						onChange: (value) =>
							dispatchState({ type: "UPDATE_FORM_FIELD", payload: { formName: "addGeneratorForm", fieldName: "address", value } }),
					},
					{
						name: "companyName",
						label: "Facility Name",
						type: "text",
						required: true,
						onChange: (value) =>
							dispatchState({ type: "UPDATE_FORM_FIELD", payload: { formName: "addGeneratorForm", fieldName: "companyName", value } }),
					},
					{
						name: "phoneNumber",
						label: "Phone Number",
						type: "text",
						required: true,
						placeholder: "Enter phone number (e.g. 0512345678)",
						regex: /^05\d{8}$/,
						regexErrorMessage: "Phone Number must start with 05 and be exactly 10 digits",
						inputMode: "numeric",
						pattern: "[0-9]*",
						numericOnly: true,
						onChange: (value) =>
							dispatchState({ type: "UPDATE_FORM_FIELD", payload: { formName: "addGeneratorForm", fieldName: "phoneNumber", value } }),
					},
				]}
			/>

			{/* Backdrops */}
			<LoadingBackdrop
				open={state.loading.isLoadingBackdrop || isDelegateLoading}
				message={t("Loading data, please wait...")}
				variant="bars"
				color="greenGradient"
			/>
		</Box>
	);
	// #endregion
}
